/* eslint-disable react/forbid-component-props */
import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/system';
import { QTabContext, QTabList, QTab, QTabPanel, MaliciousScoreChart, LibrariesAndFrameworksChartCard } from '@kw/quokka-ui';
import { RulesChart } from '../charts/RulesChart';
import { PermissionsChart } from '../charts/PermissionsChart';
import { AggregateDataProps, LevelSummary } from '../types/ReportTypes';
import { getMaliciousThreatLevel } from '../utils/dopplerUtil';
import { RulesTab } from './RulesTab';
import { LibrariesAndFrameworks } from './LibrariesAndFrameworks';
import { Permissions } from './Permissions';
import { useReloadRestore } from '../utils/useReloadRestore';

export function OverviewTab({
  aggregateData,
  ruleSummaries,
  analysisId,
  maliciousResult,
  os,
}: AggregateDataProps & { analysisId: string; ruleSummaries: any[]; maliciousResult: object; os: string }) {
  const { appDetailReportTabFocus, setAppDetailReportTabFocus, setShouldRestore, setRestoredSearchQuery } = useReloadRestore();
  const [activeTab, setActiveTab] = useState<number>(appDetailReportTabFocus.overview);

  useEffect(() => {
    setAppDetailReportTabFocus({ ...appDetailReportTabFocus, root: 0, overview: activeTab });
    setShouldRestore(false);
    setRestoredSearchQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleTabChange = (event, newTabValue) => {
    setActiveTab(newTabValue);
  };

  const libraryRelatedRuleSummaries = useMemo(
    () => ruleSummaries?.filter(({ rule }) => rule.id.includes('loads-library')),
    [ruleSummaries],
  );

  const securityOrPrivacyRuleSummaries = useMemo(() => {
    return ruleSummaries?.filter(
      ruleSummary => ruleSummary.rule?.properties?.category === 'security' || ruleSummary.rule?.properties?.category === 'privacy',
    );
  }, [ruleSummaries]);

  const levelSummary: LevelSummary[] = useMemo(() => {
    const levels: Array<string> = ['none', 'note', 'warning', 'error'];
    // Count the occurrences of each level. '0' is 'none', '1' is 'note', '2' is 'warning', and '3' is 'error'.
    const levelCounts = securityOrPrivacyRuleSummaries?.reduce<Record<string, number>>(
      (acc, rule) => {
        const levelKey = levels[rule.result_level_numeric] ?? 'none';
        acc[levelKey] = (acc[levelKey] || 0) + 1;
        return acc;
      },
      { none: 0, note: 0, warning: 0, error: 0 },
    );

    return levels.map(level => ({
      level,
      resultsCount: (levelCounts?.[level] ?? 0).toString(),
    }));
  }, [securityOrPrivacyRuleSummaries]);

  const PERMISSION_RULE_IDS = ['profile/system/requests-privileged-permissions'];

  const permissionRelatedRuleSummaries = useMemo(
    () =>
      ruleSummaries?.filter(({ rule }) => rule.tool === 'permoid' || rule.tool === 'permoid-ios' || PERMISSION_RULE_IDS.includes(rule.id)),
    [ruleSummaries],
  );
  const maliciousScore = getMaliciousThreatLevel(maliciousResult);
  const chartWidth = os !== 'android' ? '50%' : '33%';
  return (
    <Box>
      <Box display="flex" gap="16px">
        <Box sx={{ width: chartWidth }}>
          <RulesChart rulesSummary={levelSummary} legendPosition="left" legendNumColumns={1} />
        </Box>
        <Box sx={{ width: chartWidth }}>
          <PermissionsChart permissionsSummary={aggregateData?.permissionSummary} legendPosition="left" legendNumColumns={1} />
        </Box>
        {/* <Box sx={{ width: chartWidth }}>
          <LibrariesAndFrameworksChartCard
            systemLibraries={0}
            systemFrameworks={0}
            loadedLibrariesAndFrameworks={libraryRelatedRuleSummaries?.length ?? 0}
          />
        </Box> */}
        {os === 'android' && (
          <Box sx={{ width: chartWidth }}>
            <MaliciousScoreChart score={maliciousScore} />
          </Box>
        )}
      </Box>

      <QTabContext value={activeTab}>
        <QTabList
          onChange={handleTabChange}
          sx={{
            '.MuiTabs-indicator': {
              top: 0, // Moves the indicator to the top
              bottom: 'unset', // Removes default bottom positioning
            },
            '.MuiTab-root': {
              paddingTop: '16px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#757575',
              minWidth: '226px',
            },
            '.Mui-selected': {
              backgroundColor: '#FAFAFA',
              color: '#000000',
              fontWeight: '800',
            },
            backgroundColor: '#EEEEEE',
            borderBottom: 'none',
            marginTop: '40px',
          }}
        >
          <QTab label="Rules" value={0} />
          <QTab label="Permissions" value={1} />
          <QTab label="Libraries & Frameworks" value={2} />
        </QTabList>
        <QTabPanel value={0} sx={{ padding: 0 }}>
          <RulesTab ruleSummaries={securityOrPrivacyRuleSummaries} analysisId={analysisId} />
        </QTabPanel>
        <QTabPanel value={1} sx={{ padding: 0 }}>
          <Permissions ruleSummaries={permissionRelatedRuleSummaries} analysisId={analysisId} />
        </QTabPanel>
        <QTabPanel value={2} sx={{ padding: 0 }}>
          <LibrariesAndFrameworks ruleSummaries={libraryRelatedRuleSummaries} analysisId={analysisId} />
        </QTabPanel>
      </QTabContext>
    </Box>
  );
}
